import { createFileRoute, Link } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { PRAYERS, prayerMinutes, prayerPath } from "../lib/prayers";
import {
  breadcrumbJsonLd,
  canonical,
  collectionJsonLd,
  documentHeaders,
  socialMeta,
} from "../lib/seo";
import { VERSE_TOPICS } from "../lib/verses";

const TITLE = "Prayers for the moment you are in — Bible Shield";
const DESCRIPTION =
  "Written prayers for anxiety, sleep, focus, anger and the rest of an ordinary week — each with the passages behind it and what it is not asking for.";

/**
 * `/prayers` — the hub.
 *
 * ── Why this section exists ──────────────────────────────────────────────────
 *
 * `docs/BIBLE-SEARCH-DEMAND.md` §1: the search is therapeutic, not doctrinal.
 * People arrive at scripture in the middle of something, and `prayer for
 * <situation>` is the shape that arrival takes when the person has decided to
 * do something about it rather than read about it.
 *
 * It is also the section this product has the most standing to write. The app's
 * whole premise is that the phone opens after you have prayed; handing somebody
 * the words is the same act, performed for a reader who has installed nothing.
 */
export const Route = createFileRoute("/prayers/")({
  headers: documentHeaders,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...socialMeta({
        title: TITLE,
        description: DESCRIPTION,
        path: "/prayers",
        lang: "en",
      }),
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Bible Shield", path: "/" },
          { name: "Prayers", path: "/prayers" },
        ]),
      },
      {
        "script:ld+json": collectionJsonLd({
          name: TITLE,
          description: DESCRIPTION,
          path: "/prayers",
          items: PRAYERS.map((p) => ({
            name: p.h1,
            path: prayerPath(p.slug),
          })),
        }),
      },
    ],
    links: [{ rel: "canonical", href: canonical("/prayers") }],
  }),
  component: PrayersIndex,
});

function PrayersIndex() {
  return (
    <PageChrome>
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        prayers
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        Words for when you have none
      </h1>
      <p className="mt-4 max-w-[60ch] text-lg leading-relaxed text-ink2">
        Each of these is a prayer you could say out loud, written for one
        situation rather than for prayer in general. Every one names what it is
        not asking for, because a prayer page that promises the outcome is
        selling something.
      </p>

      <ul className="mt-10 grid gap-5 sm:grid-cols-2">
        {PRAYERS.map((prayer) => (
          <li key={prayer.slug}>
            <Link
              to="/prayers/$slug"
              params={{ slug: prayer.slug }}
              className="brutal flex h-full flex-col gap-3 p-6 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
                {prayerMinutes(prayer)} min
              </span>
              <span className="font-display text-xl leading-snug font-bold text-ink">
                {prayer.h1}
              </span>
              <span className="leading-relaxed text-body">
                {prayer.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <section className="mt-14 border-t-[2.5px] border-ink pt-10">
        <h2 className="font-display text-2xl font-bold text-ink">
          Looking for verses rather than words to say?
        </h2>
        <p className="mt-3 max-w-[60ch] leading-relaxed text-ink2">
          The collections are ten passages each on one theme, with a note on
          every one.
        </p>
        <ul className="mt-5 flex flex-wrap gap-3">
          {VERSE_TOPICS.map((topic) => (
            <li key={topic.slug}>
              <Link
                to="/verses/$slug"
                params={{ slug: topic.slug }}
                className="inline-flex rounded-pill border-[2.5px] border-ink bg-surface px-4 py-2 font-display text-sm font-bold text-ink transition-transform hover:-translate-y-0.5"
              >
                {topic.h1}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </PageChrome>
  );
}
